import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../utils/firebase';
import { Study, StudyParticipant, StudyTimepoint } from '../../types/study.types';
import { SurveyDefinition, DynamicSurveyResponse } from '../../types/survey.types';
import DynamicSurveyRenderer from '../../components/surveys/DynamicSurveyRenderer';
import { retryFirebaseCall } from '../../utils/retry';

interface APIResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

const StudySurvey: React.FC = () => {
  const { studyId, timepointId } = useParams<{ studyId: string; timepointId: string }>();
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('p');
  const navigate = useNavigate();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [study, setStudy] = useState<Study | null>(null);
  const [participant, setParticipant] = useState<StudyParticipant | null>(null);
  const [timepoint, setTimepoint] = useState<StudyTimepoint | null>(null);
  const [survey, setSurvey] = useState<SurveyDefinition | null>(null);

  // Success state
  const [isComplete, setIsComplete] = useState(false);
  const [responseId, setResponseId] = useState<string | null>(null);

  // Load study, participant, and survey data
  useEffect(() => {
    const loadData = async () => {
      if (!studyId || !timepointId || !participantId) {
        setError('Invalid survey link. Please check your email for the correct link.');
        setLoading(false);
        return;
      }

      try {
        // Load study
        const studyDoc = await getDoc(doc(db, 'studies', studyId));
        if (!studyDoc.exists()) {
          setError('Study not found.');
          setLoading(false);
          return;
        }
        const studyData = { ...studyDoc.data(), id: studyDoc.id } as Study;
        setStudy(studyData);

        // Find timepoint
        const tp = studyData.surveySchedule?.find((t) => t.id === timepointId);
        if (!tp) {
          setError('Survey timepoint not found.');
          setLoading(false);
          return;
        }
        setTimepoint(tp);

        // Load participant
        const participantDoc = await getDoc(
          doc(db, 'studies', studyId, 'participants', participantId)
        );
        if (!participantDoc.exists()) {
          setError('Participant not found. Please check your survey link.');
          setLoading(false);
          return;
        }
        const participantData = participantDoc.data() as StudyParticipant;
        setParticipant(participantData);

        // Check participant status
        if (participantData.status === 'withdrawn') {
          setError('You have been withdrawn from this study. Please contact the study coordinator if you have questions.');
          setLoading(false);
          return;
        }

        if (participantData.status === 'lost_to_followup') {
          setError('Your participation status is inactive. Please contact the study coordinator.');
          setLoading(false);
          return;
        }

        // Check survey progress
        const progress = participantData.surveyProgress?.[timepointId];
        if (progress) {
          // Check if already completed
          if (progress.status === 'completed') {
            setIsComplete(true);
            setResponseId(progress.responseId || null);
            setLoading(false);
            return;
          }

          // Check time window
          const now = new Date();
          const windowStart = progress.windowStart && 'toDate' in progress.windowStart
            ? progress.windowStart.toDate()
            : new Date(progress.windowStart as any);
          const windowEnd = progress.windowEnd && 'toDate' in progress.windowEnd
            ? progress.windowEnd.toDate()
            : new Date(progress.windowEnd as any);

          if (now < windowStart) {
            const daysUntil = Math.ceil((windowStart.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            setError(`This survey is not yet available. It will open in ${daysUntil} day${daysUntil === 1 ? '' : 's'}.`);
            setLoading(false);
            return;
          }

          if (now > windowEnd) {
            setError('The window for completing this survey has closed. Please contact the study coordinator.');
            setLoading(false);
            return;
          }
        }

        // Load the survey
        if (!tp.surveyId) {
          setError('No survey is configured for this timepoint.');
          setLoading(false);
          return;
        }

        const surveyDoc = await getDoc(doc(db, 'surveys', tp.surveyId));
        if (!surveyDoc.exists()) {
          setError('Survey not found.');
          setLoading(false);
          return;
        }

        const surveyData = { ...surveyDoc.data(), id: surveyDoc.id } as SurveyDefinition;

        // Check if survey is active
        if (!surveyData.settings?.isActive) {
          setError('This survey is currently unavailable.');
          setLoading(false);
          return;
        }

        setSurvey(surveyData);
        setLoading(false);
      } catch (err: any) {
        console.error('Error loading survey data:', err);
        setError('Failed to load survey. Please try again later.');
        setLoading(false);
      }
    };

    loadData();
  }, [studyId, timepointId, participantId]);

  // Handle survey completion
  const handleSurveyComplete = async (response: DynamicSurveyResponse) => {
    if (!studyId || !timepointId || !participantId || !participant || !study) {
      return;
    }

    setSubmitting(true);

    try {
      // Add study-specific metadata to the response
      const studyResponse = {
        ...response,
        metadata: {
          ...response.metadata,
          studyId,
          participantId,
          participantCode: participant.participantCode,
          timepointId,
          timepointName: timepoint?.name || '',
          isStudyResponse: true,
        },
      };

      // Submit the response using the existing function
      const submitResponseFn = httpsCallable<DynamicSurveyResponse, APIResponse<string>>(
        functions,
        'submitResponse'
      );

      const result = await retryFirebaseCall(
        () => submitResponseFn(studyResponse),
        'submitResponse',
        { maxAttempts: 2 }
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to submit response');
      }

      const newResponseId = result.data.data;

      // Update participant's survey progress
      const now = Timestamp.now();
      await updateDoc(
        doc(db, 'studies', studyId, 'participants', participantId),
        {
          [`surveyProgress.${timepointId}.status`]: 'completed',
          [`surveyProgress.${timepointId}.completedAt`]: now,
          [`surveyProgress.${timepointId}.responseId`]: newResponseId,
          lastActivityAt: now,
        }
      );

      // Update participant status if this completes all surveys
      const allTimepoints = study.surveySchedule || [];
      const updatedProgress = { ...participant.surveyProgress };
      updatedProgress[timepointId] = {
        ...updatedProgress[timepointId],
        status: 'completed',
      };

      const allCompleted = allTimepoints.every(
        (tp) => updatedProgress[tp.id]?.status === 'completed'
      );

      if (allCompleted) {
        await updateDoc(
          doc(db, 'studies', studyId, 'participants', participantId),
          { status: 'completed' }
        );

        // Update study analytics
        await updateDoc(doc(db, 'studies', studyId), {
          'analytics.activeParticipants': (study.analytics?.activeParticipants || 1) - 1,
          'analytics.completedParticipants': (study.analytics?.completedParticipants || 0) + 1,
        });
      }

      setResponseId(newResponseId);
      setIsComplete(true);
    } catch (err: any) {
      console.error('Error submitting survey:', err);
      alert(`Failed to submit survey: ${err.message || 'Please try again'}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to leave? Your progress may not be saved.')) {
      navigate('/');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your survey...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Survey Unavailable</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <a
              href="/"
              className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Already completed state
  if (isComplete) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Survey Completed!</h1>
            <p className="text-gray-600 mb-4">
              Thank you for completing the {timepoint?.name || 'survey'} for {study?.title}.
            </p>
            {participant && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-xs text-gray-500 mb-1">Participant Code</p>
                <p className="font-mono font-semibold text-gray-900">{participant.participantCode}</p>
              </div>
            )}
            {responseId && (
              <div className="text-xs text-gray-500 mb-6">
                Reference: {responseId}
              </div>
            )}
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start text-left">
                <svg className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Your response has been securely recorded</span>
              </div>
              <div className="flex items-start text-left">
                <svg className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>You'll receive your next survey link by email when it's time</span>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                You may close this window.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Survey not loaded
  if (!survey) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto text-center">
          <p className="text-gray-600">Unable to load survey.</p>
        </div>
      </div>
    );
  }

  // Render survey
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Study/Timepoint Header */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 mb-6">
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-sm font-medium text-teal-800">{study?.title}</p>
              <p className="text-xs text-teal-600">{timepoint?.name} Survey</p>
            </div>
            {participant && (
              <div className="text-right">
                <p className="text-xs text-teal-600">Participant</p>
                <p className="font-mono text-sm font-semibold text-teal-800">
                  {participant.participantCode}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submitting Overlay */}
      {submitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Submitting your response...</p>
          </div>
        </div>
      )}

      {/* Survey Renderer */}
      <DynamicSurveyRenderer
        surveyDefinition={survey}
        onComplete={handleSurveyComplete}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default StudySurvey;
